import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  Typography, 
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import { MyLocation as MyLocationIcon, Search as SearchIcon, Home as HomeIcon } from '@mui/icons-material';
import API_BASE_URL from '../config';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Maps.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const homeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const Maps = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [primaryColor, setPrimaryColor] = useState('#000000');
  const [secondaryColor, setSecondaryColor] = useState('#ffffff');
  const [darkMode, setDarkMode] = useState(false);
  const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [mapCenter, setMapCenter] = useState([-27.085205396426563, 152.9801761537565]);
  const [packageAllowance, setPackageAllowance] = useState(false);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const checkLocalStorage = () => {
      const storedPrimaryColor = localStorage.getItem('primaryColor');
      const storedSecondaryColor = localStorage.getItem('secondaryColor');
      const storedDarkMode = localStorage.getItem('darkMode') === 'true';

      if (storedPrimaryColor !== primaryColor) {
        setPrimaryColor(storedPrimaryColor);
      }
      if (storedSecondaryColor !== secondaryColor) {
        setSecondaryColor(storedSecondaryColor);
      }
      if (storedDarkMode !== darkMode) {
        setDarkMode(storedDarkMode);
        setTextColor(storedDarkMode ? '#ffffff' : '#000000');
      }
    };
    checkLocalStorage();
    const intervalId = setInterval(checkLocalStorage, 200);

    return () => clearInterval(intervalId);
  }, [primaryColor, secondaryColor, darkMode]);

  const fetchPackageAllowance = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/package/gps`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setPackageAllowance(data === 1);

    } catch (error) {
      console.error('Error fetching user package:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/locations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch device locations');
      const data = await response.json();
      
      // Use last known lat and lon for each device
      const devicesWithPosition = data.map(device => ({
        ...device,
        position: {
          lat: parseFloat(device.last_known_lat),
          lng: parseFloat(device.last_known_lon)
        }
      }));
    
      setDevices(devicesWithPosition);


    } catch (error) {
      console.error('Error fetching device locations:', error);
    }
  }, [userLocation]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userLoc);
          setMapCenter([userLoc.lat, userLoc.lng]);
        },
        () => {
          console.error('Error: The Geolocation service failed.');
          // Fetch devices if geolocation fails
          fetchDevices();
        }
      );
    } else {
      console.error('Error: Your browser doesn\'t support geolocation.');
      // Fetch devices if geolocation is not supported
      fetchDevices();
    }
  };

  useEffect(() => {
    fetchPackageAllowance();
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    const filtered = devices.filter(device => 
      device.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.street_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.suburb.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.postcode.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredDevices(filtered);
  }, [searchTerm, devices]);

  const parsePosition = (lonNum, lonLetter, latNum, latLetter) => {
    const lon = parseFloat(lonNum);
    const lat = parseFloat(latNum);
    return {
      lng: (lonLetter === 'E' ? 1 : -1) * (lon > 360 ? 360 : lon),
      lat: (latLetter === 'N' ? 1 : -1) * (lat > 90 ? 90 : lat)
    };
  };

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    if (mapRef.current) {
      mapRef.current.flyTo([device.position.lat, device.position.lng], 13, {
        duration: 1
      });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };


  const handleBackToUser = () => {
    if (userLocation) {
      setSelectedDevice(null);
      if (mapRef.current) {
        mapRef.current.setView([userLocation.lat, userLocation.lng], 13);
      }
    }
  };

  const MapEvents = () => {
    const map = useMap();
    
    useEffect(() => {
      mapRef.current = map;
    }, [map]);

    return null;
  };

  return (
    <div className="maps-container" style={{ backgroundColor: secondaryColor }}>
      {loading ? (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: secondaryColor,
          color: textColor,
          padding: 20,
          textAlign: 'center'
        }}>
          <CircularProgress />
        </div>
      ) : !packageAllowance ? (
        <div style={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: secondaryColor,
          color: textColor,
          padding: 20,
          textAlign: 'center'
        }}>
          <div>
            <Typography variant="h4" gutterBottom>
              Map Access Restricted
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/help')}
              sx={{ 
                mt: 2,
                backgroundColor: primaryColor,
                color: textColor,
                '&:hover': {
                  backgroundColor: darkMode ? '#555' : '#e0e0e0',
                }
              }}
            >
              Contact Support
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Paper className="device-list" style={{ maxHeight: '100vh', overflow: 'auto', padding: '10px', backgroundColor: secondaryColor }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search devices..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{ 
              marginBottom: '10px', 
              backgroundColor: darkMode ? '#424242' : '#ffffff',
              color: textColor
            }}
          />
          <Grid container spacing={2}>
            {filteredDevices.map((device) => (
              <Grid item xs={12} key={device.device_id}>
                <Card 
                  onClick={() => handleDeviceClick(device)}
                  style={{ 
                    backgroundColor: primaryColor, 
                    color: textColor,
                    cursor: 'pointer',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <CardContent style={{ padding: '12px 16px' }}>
                    <Typography variant="h6" component="h2" style={{ fontWeight: 'bold', marginBottom: '8px', color: textColor }}>
                      {device.device_id}
                    </Typography>
                    <Typography 
                      variant="body" 
                      component="p" 
                      style={{ 
                        marginBottom: '6px',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        width: '100%'
                      }}
                    >
                      {device.street_address}, {device.suburb}, {device.state}, {device.country}, {device.postcode}
                    </Typography>
                    <Typography variant="body2" component="p" style={{ marginBottom: '4px' }}>
                      Direction: {device.direction}
                    </Typography>
                    <Typography variant="body2" component="p">
                      Description: {device.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
        <div className="map-container">
          <MapContainer 
            center={userLocation || [-27.085205396426563, 152.9801761537565]}
            zoom={11} 
            style={{ width: '100%', height: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <MapEvents />
            {userLocation && (
              <Marker position={[userLocation.lat, userLocation.lng]} icon={homeIcon}>
                <Popup>You are here</Popup>
              </Marker>
            )}
            {devices.map((device) => (
              <Marker
                key={device.device_id}
                position={[device.position.lat, device.position.lng]}
                eventHandlers={{
                  click: () => handleDeviceClick(device),
                }}
              >
                <Popup>
                  <h3>{device.device_id}</h3>
                  <p>{device.street_address}, {device.suburb}, {device.state} {device.postcode}, {device.country}</p>
                  <p>Direction: {device.direction}</p>
                  <p>Description: {device.description}</p>
                  <p>Location: {device.position.lat.toFixed(6)}, {device.position.lng.toFixed(6)}</p>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          {/*
            <Button 
              className="get-location-button"
              onClick={getUserLocation}
              variant="contained"
              startIcon={<MyLocationIcon />}
              style={{ 
                position: 'absolute', 
                bottom: '20px', 
                right: '20px',
                backgroundColor: primaryColor, 
                color: textColor,
                zIndex: 1000
              }}
            >
              Get My Location
            </Button>
          */}
          </div>
        </>
      )}
        

    </div>
  );
};

export default Maps;